import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
  /** Optional second line — what the option means, when the label cannot say it. */
  hint?: string;
  /** Tints the label and the tick. Used for priority and status, where colour is data. */
  color?: string;
}

/**
 * A dropdown we own.
 *
 * A native <select> cannot be styled where it matters: the popup list is drawn by the
 * operating system, so it ignores the theme entirely and renders a white menu over a
 * black board. It also cannot show a colour swatch, a second line, or a tick — and
 * "urgent" without its red is just a word.
 *
 * What the native element gives you for free has to be rebuilt deliberately, and this
 * does: keyboard control, an accessible name, one-open-at-a-time, and Escape.
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  placeholder = 'Any',
  label,
  className = '',
}: {
  value: T | null;
  options: ReadonlyArray<DropdownOption<T>>;
  onChange: (value: T | null) => void;
  /** Shown when nothing is selected. Selecting it again clears the choice. */
  placeholder?: string;
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((option) => option.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    // Open onto the current choice, so Enter without arrowing is a no-op rather than
    // silently picking whatever happens to be first.
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function choose(next: T | null) {
    onChange(next);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setOpen(true);
      return;
    }

    if (!open) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // Wraps, because a list that stops dead at the end makes you reverse all the way
      // back to reach the option one step the other side of where you started.
      setActive((i) => (i + step + options.length) % options.length);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = options[active];
      if (option) choose(option.value === value ? null : option.value);
    }
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className="flex min-h-9 w-full items-center gap-2 rounded-lg border px-2.5 text-left text-sm"
        style={{
          borderColor: open ? 'var(--accent)' : 'var(--border)',
          background: 'var(--surface-sunken)',
          color: selected ? 'var(--text)' : 'var(--text-faint)',
        }}
      >
        {selected?.color && (
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{ background: selected.color }}
          />
        )}
        <span className="min-w-0 flex-1 truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown
          size={14}
          className="shrink-0 transition-transform"
          style={{
            color: 'var(--text-faint)',
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto no-scrollbar rounded-lg border py-1"
          style={{
            borderColor: 'var(--border)',
            background: 'var(--surface)',
            boxShadow: 'var(--shadow-panel)',
          }}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onPointerEnter={() => setActive(index)}
                  // Selecting the current value clears it — the same gesture that set it.
                  onClick={() => choose(isSelected ? null : option.value)}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm"
                  style={{ background: index === active ? 'var(--surface-sunken)' : 'transparent' }}
                >
                  {option.color && (
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: option.color }}
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate" style={option.color ? { color: option.color } : undefined}>
                      {option.label}
                    </span>
                    {option.hint && (
                      <span className="block truncate text-[11px]" style={{ color: 'var(--text-faint)' }}>
                        {option.hint}
                      </span>
                    )}
                  </span>
                  {isSelected && <Check size={14} style={{ color: 'var(--accent)' }} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
